class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.isLoading = false;
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.debounceTimeout = null;
    this.latestSearchData = null;


    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!newSearchTerm.length) {
      this.close(true);
      return;
    }

    if (this.searchTerm.length > newSearchTerm.length) {
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    }

    this.searchTerm = newSearchTerm;

    this.debounceTimeout = setTimeout(() => {
      if (this.searchTerm.length < 3) return;

      if (this.isOpen) {
        this.getSearchResults(this.searchTerm);
      } else {
        this.open();
        this.getSearchResults(this.searchTerm);
      }
    }, 200);
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (this.isLoading || !query.length) {
      event.preventDefault();
      return;
    }

    const selected = this.querySelector('[aria-selected="true"] a');
    if (selected) {
      selected.click();
      event.preventDefault();
      return;
    }

    if (this.latestSearchData && this.latestSearchData.redirect) {
      event.preventDefault();
      window.location.href = this.latestSearchData.redirect;
    }
  }


  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
        (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }


    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
      selectedElement.classList.remove('is-active');
    }

    activeElement.classList.add('is-active');

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {

    if (!searchTerm || searchTerm.trim().length < 3) {
      return;
    }

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    fetch(
        `https://autocomplete.shp.groupbycloud.com/api/request?collection=Production&area=Production&searchItems=10&query=${encodeURIComponent(searchTerm)}&dataset=user-data`,
        {
          method: "GET",
          headers: {
            "Authorization": window.groupbyKey,
            "Content-Type": "application/json",
            "X-Groupby-Customer-ID": window.groupbyCustomerId
          },
          signal: this.abortController?.signal
        }
    )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(async (autocompleteData) => {
          const data = await this.fetchTopProducts(searchTerm);
          if (!data) return;

          this.latestSearchData = data;

          const products = data.records || [];
          this.initializeResultsContainer();
          if (!this.isOpen) {
            this.open();
          }

          this.renderSearchResults({ autocompleteData, products });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Error fetching autocomplete suggestions:", error);
            this.close();
          }
        });
  }

  async fetchTopProducts(query) {
    if (!query || query.length < 2) return [];

    const url = "https://search.shp.groupbycloud.com/api/search";

    const payload = {
      query: query,
      collection: "Production",
      area: "Production",
      pageSize: 5,
      skip: 0,
      fields: ["*"]
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": window.groupbyKey,
          "Content-Type": "application/json",
          "Skip-Caching": "true",
          "X-Groupby-Customer-Id": window.groupbyCustomerId
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      return data;

    } catch (error) {
      console.error("Error fetching top products:", error);
      return [];
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
  }

  initializeResultsContainer() {
    if (!this.predictiveSearchResults) {
      console.error("Predictive search container not found.");
      return;
    }

    if (!this.predictiveSearchResults.querySelector("#predictive-search-results-groups-wrapper")) {
      this.predictiveSearchResults.innerHTML = `
            <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
                <div class="predictive-search__result-group">
                    <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">
                        Suggestions
                    </h2>
                    <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries"></ul>
                </div>
                <div class="predictive-search__result-group">
                    <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
                        Products
                    </h2>
                    <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="list" aria-labelledby="predictive-search-products"></ul>
                </div>
            </div>
        `;
    }
  }

  renderSearchResults({ autocompleteData, products }) {
    if (!this.predictiveSearchResults) {
      console.error("predictiveSearchResults element not found");
      return;
    }

    const queriesContainer = this.predictiveSearchResults.querySelector("#predictive-search-results-queries-list");
    const productsContainer = this.predictiveSearchResults.querySelector("#predictive-search-results-products-list");
    const queriesHeader = this.predictiveSearchResults.querySelector("#predictive-search-queries");
    const productsHeader = this.predictiveSearchResults.querySelector("#predictive-search-products");

    if (!queriesContainer || !productsContainer || !queriesHeader || !productsHeader) {
      console.warn("Missing query or product containers.");
      return;
    }

    queriesContainer.innerHTML = "";
    productsContainer.innerHTML = "";

    if (autocompleteData.searchTerms?.length) {
      queriesHeader.style.display = "block";
      autocompleteData.searchTerms.forEach((result, index) => {
        const listItem = document.createElement("li");
        listItem.classList.add("predictive-search__list-item");
        listItem.setAttribute("role", "listitem");
        listItem.setAttribute("aria-selected", "false");
        listItem.id = `predictive-search-option-query-${index + 1}`;

        const queryParam = encodeURIComponent(result.value);
        const searchUrl = `/search?q=${queryParam}&options[prefix]=last`;

        const link = document.createElement("a");
        link.href = searchUrl;
        link.classList.add("predictive-search__item", "link", "link--text");
        link.tabIndex = -1;

        const contentDiv = document.createElement("div");
        contentDiv.classList.add("predictive-search__item-content", "predictive-search__item-content--centered");

        const heading = document.createElement("p");
        heading.classList.add("predictive-search__item-heading", "predictive-search__item-query-result", "h5");
        heading.setAttribute("aria-label", result.value);
        heading.innerHTML = this.highlightSearchTerm(result.value);

        contentDiv.appendChild(heading);
        link.appendChild(contentDiv);
        listItem.appendChild(link);
        queriesContainer.appendChild(listItem);
      });
    } else {
      queriesHeader.style.display = "none";
    }

    if (products.length > 0) {
      productsHeader.style.display = "block";
      this.renderProductItems(products, productsContainer);
    } else {
      productsHeader.style.display = "none";
    }

    this.setLiveRegionResults();
    this.addSearchButton();
  }

  async renderProductItems(products, container) {
    for (const product of products) {
      const productItem = await this.createProductItem(product);
      container.appendChild(productItem);
    }
  }

  async createProductItem(product) {
    const productItem = document.createElement("li");
    productItem.classList.add("predictive-search__list-item");
    productItem.setAttribute("role", "listitem");
    productItem.setAttribute("aria-selected", "false");

    // Find matching variant based on SKU
    const matchingVariant = this.findMatchingVariantSku(product);

    // Build product URL with variant if found
    let productUrl = product.uri || `/products/${product.allMeta.attributes.handle.text[0]}`;
    if (matchingVariant) {
      const separator = productUrl.includes('?') ? '&' : '?';
      productUrl += `${separator}variant=${matchingVariant.id}`;
    }

    const productLink = document.createElement("a");
    productLink.href = productUrl;
    productLink.classList.add("predictive-search__item", "predictive-search__item--link-with-thumbnail", "link", "link--text");
    productLink.tabIndex = -1;

    // Fetch the product card HTML to get the correct variant image
    let imageUrl = null;
    let imageAlt = product.allMeta.attributes.title.text[0];

    if (matchingVariant) {
      try {
        const handle = product.allMeta.attributes.handle.text[0];
        const cardUrl = `/products/${handle}?view=card&variant=${matchingVariant.id}`;
        const responseText = await fetch(cardUrl).then((response) => response.text());
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const cardImage = html.querySelector('.collection-banner_recently-viewed img');

        if (cardImage) {
          imageUrl = cardImage.src;
          imageAlt = cardImage.alt || imageAlt;
        }
      } catch (error) {
        console.error("Error fetching product card:", error);
      }
    }

    // Fallback to default image if variant image not found
    if (!imageUrl && product.allMeta.images?.length > 0) {
      imageUrl = product.allMeta.images[0].uri;
    }

    if (imageUrl) {
      const productImage = document.createElement("img");
      productImage.src = imageUrl;
      productImage.alt = imageAlt;
      productImage.classList.add("predictive-search__image");
      productImage.width = 50;
      productImage.height = 50;
      productLink.appendChild(productImage);
    }

    const contentDiv = document.createElement("div");
    contentDiv.classList.add("predictive-search__item-content", "predictive-search__item-content--centered");

    const productTitle = document.createElement("p");
    productTitle.classList.add("predictive-search__item-heading", "h5");

    // Show variant title if available
    let displayTitle = product.allMeta.attributes.title.text[0] || "Unknown Product";
    if (matchingVariant && matchingVariant.title) {
      displayTitle = matchingVariant.title;
    }
    displayTitle = displayTitle.replace(/\s*-\s*Default Title\s*/i, "");

    productTitle.textContent = displayTitle;

    contentDiv.appendChild(productTitle);
    productLink.appendChild(contentDiv);
    productItem.appendChild(productLink);

    return productItem;
  }

  findMatchingVariantSku(record) {
    const normalizedQuery = this.searchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[+\-_]/g, '');

    if (record.allMeta?.variants && Array.isArray(record.allMeta.variants)) {
      for (const variant of record.allMeta.variants) {
        const variantSku = variant.attributes?.sku?.text?.[0];

        if (variantSku) {
          const normalizedSku = String(variantSku)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/[+\-_]/g, '');

          if (normalizedSku === normalizedQuery) {
            return {
              id: variant.id,
              sku: variantSku,
              title: variant.attributes?.title?.text?.[0]
            };
          }
        }
      }
    }

    return null;
  }

  highlightSearchTerm(text) {
    const searchTerm = this.searchTerm.trim();
    if (!searchTerm.length) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  }

  addSearchButton() {
    const existingButton = this.predictiveSearchResults.querySelector("#predictive-search-option-search-keywords");
    if (existingButton) existingButton.remove();

    const buttonContainer = document.createElement("div");
    buttonContainer.id = "predictive-search-option-search-keywords";
    buttonContainer.classList.add("predictive-search__search-for-button");

    const button = document.createElement("button");
    button.classList.add("predictive-search__item", "predictive-search__item--term", "link", "link--text", "h5", "animate-arrow");
    button.tabIndex = -1;
    button.setAttribute("role", "listitem");
    button.setAttribute("aria-selected", "false");

    const span = document.createElement("span");
    span.setAttribute("data-predictive-search-search-for-text", "");
    span.textContent = `Search for “${this.searchTerm}”`;

    const arrowSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrowSvg.setAttribute("viewBox", "0 0 14 10");
    arrowSvg.setAttribute("fill", "none");
    arrowSvg.setAttribute("aria-hidden", "true");
    arrowSvg.setAttribute("focusable", "false");
    arrowSvg.classList.add("icon", "icon-arrow");
    arrowSvg.innerHTML = `<path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path>`;

    button.appendChild(span);
    button.appendChild(arrowSvg);
    buttonContainer.appendChild(button);

    this.predictiveSearchResults.appendChild(buttonContainer);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionCountElement = this.querySelector('[data-predictive-search-live-region-count-value]');

    if (liveRegionCountElement) {
      const countText = liveRegionCountElement.textContent.trim();
      if (countText.length > 0) {
        this.setLiveRegionText(countText);
      } else {
        console.warn("Live region count text is empty.");
      }
    } else {
      console.warn("Element [data-predictive-search-live-region-count-value] not found.");
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
